import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useTheme } from '@mui/material/styles';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import enhancedFinancialDataService from '../services/EnhancedFinancialDataService';

const MetricTable = ({ ticker, exchange }) => {
  const theme = useTheme();
  const [financialData, setFinancialData] = useState(null);
  const [hoveredPeriod, setHoveredPeriod] = useState(null);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [clickedPeriod, setClickedPeriod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [metrics, setMetrics] = useState([
    { Metric: "Revenue", Value: "", "Clicked Period": "", Change: "" },
    { Metric: "Gross Margin", Value: "", "Clicked Period": "", Change: "" },
    { Metric: "Operating Income (EBITDA)", Value: "", "Clicked Period": "", Change: "" },
    { Metric: "Earnings (Net income)", Value: "", "Clicked Period": "", Change: "" },
    { Metric: "Profit Margin", Value: "", "Clicked Period": "", Change: "" },
    { Metric: "Free cashflow", Value: "", "Clicked Period": "", Change: "" }
  ]);
  
  const [error, setError] = useState(null);
  const [currency, setCurrency] = useState('USD');
  const [financialPeriodType, setFinancialPeriodType] = useState('quarterly');

  const normalizeCurrencyCode = (value) => {
    const raw = String(value || '').trim().toUpperCase();
    if (!raw) return 'USD';
    if (raw === 'RMB') return 'CNY';
    if (raw === 'NT$' || raw === 'TWD$' || raw === 'TW$') return 'TWD';
    const compact = raw.replace(/[^A-Z]/g, '');
    if (compact.includes('USD')) return 'USD';
    if (compact.includes('TWD')) return 'TWD';
    return compact.length === 3 ? compact : raw;
  };

  const convertRowsToUsd = (rows, sourceCurrency, rateToUsd) => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    if (sourceCurrency === 'USD' || !Number.isFinite(rateToUsd) || rateToUsd <= 0) return rows;

    const monetaryKeys = [
      'Revenue',
      'Operating Income',
      'Gross Profit',
      'Net Income',
      'Free Cash Flow',
    ];

    return rows.map((row) => {
      const converted = { ...row };
      monetaryKeys.forEach((key) => {
        const value = Number(converted[key]);
        if (Number.isFinite(value)) {
          converted[key] = value * rateToUsd;
        }
      });
      return converted;
    });
  };

  // Currency symbols for display
  const currencySymbols = {
    USD: '$',
    JPY: '¥',
    EUR: '€',
    GBP: '£',
    CNY: '¥',
    TWD: 'NT$',
    KRW: '₩',
    INR: '₹',
    BRL: 'R$',
    CAD: 'C$',
    AUD: 'A$',
    CHF: 'Fr',
    RMB: '¥',
  };
  
  const getCurrencySymbol = () => currencySymbols[currency] || currency || '$';

  // Sort data by period in ascending order (oldest to newest)
  const sortByPeriodAscending = (data) => {
    if (!data) return [];
    return [...data].sort((a, b) => new Date(a.period) - new Date(b.period));
  };

  const fetchFinancialData = async () => {
    if (!ticker) return;      setLoading(true);
    setError(null);
    try {
      // Use our centralized financial data service instead of direct API call
      const response = await enhancedFinancialDataService.getFinancialData(ticker, false, financialPeriodType);
      
      // Get currency for this ticker
      const tickerCurrency = normalizeCurrencyCode(
        enhancedFinancialDataService.getCurrency(ticker, financialPeriodType)
      );
      let rateToUsd = 1;

      if (tickerCurrency !== 'USD') {
        try {
          const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
          const rateResp = await fetch(`${apiBaseUrl}/api/exchange-rate/${tickerCurrency}/USD`);
          if (rateResp.ok) {
            const rateData = await rateResp.json();
            const parsedRate = Number(rateData?.rate);
            if (Number.isFinite(parsedRate) && parsedRate > 0) {
              rateToUsd = parsedRate;
            }
          }
        } catch (rateError) {
          console.warn(`MetricTable: Failed to fetch ${tickerCurrency}->USD rate`, rateError);
        }
      }

      // Metrics tab defaults to USD display.
      setCurrency('USD');
      
      if (response && Array.isArray(response) && response.length > 0) {
        console.log('Fetched financial data:', response);
        
        // Sort data by period in ascending order (oldest to newest)
        const usdRows = convertRowsToUsd(response, tickerCurrency, rateToUsd);
        const sortedData = sortByPeriodAscending(usdRows);
        setFinancialData(sortedData);
        
        // Set initial hovered period to most recent period (last item after sorting)
        const mostRecentPeriod = sortedData[sortedData.length - 1].period;
        setHoveredPeriod(mostRecentPeriod);
        fetchMetricsForPeriod(mostRecentPeriod);
      } else {
        console.warn('API returned empty or invalid data');
        setError("API returned empty or invalid data. Please try again later.");
        setFinancialData([]);
      }
    } catch (error) {
      console.error('Error fetching financial data:', error);
      setError("Failed to fetch financial data. Please try again later.");
      setFinancialData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMetricsForPeriod = async (period) => {
    if (!ticker || !period || !financialData) return;
    
    try {
      // Instead of making an API call, use the same financialData that the bar chart uses
      const periodData = financialData.find(item => item.period === period);
      
      if (!periodData) {
        // This can happen during transient hover events; do not show a sticky warning.
        console.warn('No financial data found for period:', period);
        return;
      }

      // Clear any stale warning once a valid period is resolved.
      setError(null);
      
      console.log('Using financial data for metrics:', periodData);
      
      // Extract and calculate metrics from the financial data
      const metricsData = {
        revenue: periodData.Revenue || 0,
        operating_income: periodData['Operating Income'] || 0,
        // Calculate other metrics based on available financial data
        gross_margin: calculateGrossMargin(periodData),
        earnings: calculateEarnings(periodData),
        profit_margin: calculateProfitMargin(periodData),
        free_cashflow: calculateFreeCashflow(periodData)
      };
      
      if (comparisonMode && clickedPeriod) {
        const clickedPeriodData = financialData.find(item => item.period === clickedPeriod);
        
        if (!clickedPeriodData) {
          // Keep chart usable if selected comparison period is no longer available.
          console.warn('No financial data found for clicked period:', clickedPeriod);
          return;
        }
        
        const clickedMetricsData = {
          revenue: clickedPeriodData.Revenue || 0,
          operating_income: clickedPeriodData['Operating Income'] || 0,
          gross_margin: calculateGrossMargin(clickedPeriodData),
          earnings: calculateEarnings(clickedPeriodData),
          profit_margin: calculateProfitMargin(clickedPeriodData),
          free_cashflow: calculateFreeCashflow(clickedPeriodData)
        };
        
        updateMetricsWithComparison(metricsData, clickedMetricsData);
      } else {
        updateMetrics(metricsData);
      }
    } catch (error) {
      console.error('Error processing financial data for metrics:', error);
      setError('Failed to process financial data for metrics');
    }
  };

  // Helper functions to calculate metrics from financial data
  const calculateGrossMargin = (data) => {
    // If Gross Profit is available, use it
    if (data['Gross Profit'] && data.Revenue) {
      return (data['Gross Profit'] / data.Revenue) * 100;
    }
    // Otherwise estimate (typically 30-50% for many companies)
    return data.Revenue ? (data['Operating Income'] / data.Revenue) * 1.5 * 100 : 0;
  };

  const calculateEarnings = (data) => {
    // If Net Income is available, use it
    if (data['Net Income']) {
      return data['Net Income'];
    }
    // Otherwise estimate (typically 15-25% of operating income)
    return data['Operating Income'] ? data['Operating Income'] * 0.8 : 0;
  };

  const calculateProfitMargin = (data) => {
    const earnings = data['Net Income'] || calculateEarnings(data);
    return data.Revenue ? (earnings / data.Revenue) * 100 : 0;
  };

  const calculateFreeCashflow = (data) => {
    // If Free Cash Flow is available, use it
    if (data['Free Cash Flow']) {
      return data['Free Cash Flow'];
    }
    // Otherwise estimate (typically 80-90% of operating income for stable companies)
    return data['Operating Income'] ? data['Operating Income'] * 0.85 : 0;
  };

  // Format metrics for normal mode
  const updateMetrics = (data) => {
    console.log('Formatting metrics with data:', data);
    
    const updatedMetrics = [
      { 
        Metric: "Revenue", 
        Value: formatCurrency(data.revenue),
        "Clicked Period": "",
        Change: "" 
      },
      { 
        Metric: "Gross Margin", 
        Value: `${data.gross_margin.toFixed(2)}%`,
        "Clicked Period": "",
        Change: "" 
      },
      { 
        Metric: "Operating Income (EBITDA)", 
        Value: formatCurrency(data.operating_income),
        "Clicked Period": "",
        Change: "" 
      },
      { 
        Metric: "Earnings (Net income)", 
        Value: formatCurrency(data.earnings),
        "Clicked Period": "",
        Change: "" 
      },
      { 
        Metric: "Profit Margin", 
        Value: `${data.profit_margin.toFixed(2)}%`,
        "Clicked Period": "",
        Change: "" 
      },
      { 
        Metric: "Free cashflow", 
        Value: formatCurrency(data.free_cashflow),
        "Clicked Period": "",
        Change: "" 
      }
    ];
    
    setMetrics(updatedMetrics);
  };

  // Format metrics for comparison mode
  const updateMetricsWithComparison = (currentData, clickedData) => {
    const calculateChange = (current, previous) => {
      if (!previous || previous === 0) return "N/A";
      const percentChange = ((current - previous) / Math.abs(previous)) * 100;
      return `${percentChange.toFixed(2)}%`;
    };
    
    const updatedMetrics = [
      { 
        Metric: "Revenue", 
        Value: formatCurrency(currentData.revenue), 
        "Clicked Period": formatCurrency(clickedData.revenue), 
        Change: calculateChange(currentData.revenue, clickedData.revenue) 
      },
      { 
        Metric: "Gross Margin", 
        Value: `${currentData.gross_margin.toFixed(2)}%`, 
        "Clicked Period": `${clickedData.gross_margin.toFixed(2)}%`, 
        Change: calculateChange(currentData.gross_margin, clickedData.gross_margin)
      },
      { 
        Metric: "Operating Income (EBITDA)", 
        Value: formatCurrency(currentData.operating_income), 
        "Clicked Period": formatCurrency(clickedData.operating_income), 
        Change: calculateChange(currentData.operating_income, clickedData.operating_income)
      },
      { 
        Metric: "Earnings (Net income)", 
        Value: formatCurrency(currentData.earnings), 
        "Clicked Period": formatCurrency(clickedData.earnings), 
        Change: calculateChange(currentData.earnings, clickedData.earnings)
      },
      { 
        Metric: "Profit Margin", 
        Value: `${currentData.profit_margin.toFixed(2)}%`, 
        "Clicked Period": `${clickedData.profit_margin.toFixed(2)}%`, 
        Change: calculateChange(currentData.profit_margin, clickedData.profit_margin)
      },
      { 
        Metric: "Free cashflow", 
        Value: formatCurrency(currentData.free_cashflow), 
        "Clicked Period": formatCurrency(clickedData.free_cashflow), 
        Change: calculateChange(currentData.free_cashflow, clickedData.free_cashflow)
      }
    ];
    
    setMetrics(updatedMetrics);
  };

  // Format values for display with appropriate units
  const formatMetricValue = (metric, value) => {
    if (!value && value !== 0) return "N/A";
    
    if (metric.includes("Margin")) {
      return `${parseFloat(value).toFixed(2)}%`;
    }
    
    // Format currency values
    return formatCurrency(value);
  };

  const formatCurrency = (value) => {
    const symbol = getCurrencySymbol();
    // Improved error handling to catch all non-numeric cases
    if (value === null || value === undefined || isNaN(Number(value))) {
      console.warn('Invalid currency value:', value);
      return `${symbol}0.00`;
    }
    
    // Ensure value is treated as a number
    const numValue = Number(value);
    
    // Format with appropriate units (B, M, etc.)
    if (Math.abs(numValue) >= 1e12) return `${symbol}${(numValue / 1e12).toFixed(2)}T`;
    if (Math.abs(numValue) >= 1e9) return `${symbol}${(numValue / 1e9).toFixed(2)}B`;
    if (Math.abs(numValue) >= 1e6) return `${symbol}${(numValue / 1e6).toFixed(2)}M`;
    if (Math.abs(numValue) >= 1e3) return `${symbol}${(numValue / 1e3).toFixed(2)}K`;
    return `${symbol}${numValue.toFixed(2)}`;
  };

  useEffect(() => {
    fetchFinancialData();
  }, [ticker, exchange, financialPeriodType]);

  const handleBarHover = (data) => {
    if (!data || !Array.isArray(data.activePayload) || data.activePayload.length === 0) return;

    const period = data.activePayload[0]?.payload?.period;
    if (!period) return;

    setHoveredPeriod(period);
    fetchMetricsForPeriod(period);
  };

  const handleBarClick = (data) => {
    if (!data || !Array.isArray(data.activePayload) || data.activePayload.length === 0) return;

    const period = data.activePayload[0]?.payload?.period;
    if (!period) return;

    setClickedPeriod(period);
    setComparisonMode(true);
    fetchMetricsForPeriod(period);
  };

  const handleExitComparison = () => {
    setComparisonMode(false);
    setClickedPeriod(null);
    if (hoveredPeriod) {
      fetchMetricsForPeriod(hoveredPeriod);
    }
  };

  const formatPeriodHeader = (period) => {
    if (!period) return '';
    const parsed = new Date(period);
    if (Number.isNaN(parsed.getTime())) return String(period);
    return parsed.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const selectedPeriodHeader = clickedPeriod
    ? formatPeriodHeader(clickedPeriod)
    : 'Selected Period';
  const activePeriodHeader = hoveredPeriod
    ? formatPeriodHeader(hoveredPeriod)
    : 'Active Period';

  // Get data range for any data key to properly handle negative values
  const getDataRange = (data, key) => {
    if (!data || data.length === 0) return { min: 0, max: 0 };
    
    const values = data.map(item => Number(item[key]) || 0);
    const min = Math.min(...values);
    const max = Math.max(...values);
    
    // Handle different scenarios for better scaling
    if (min >= 0) {
      // All positive values - start at 0
      return { min: 0, max: max * 1.1 }; // Add 10% padding at top
    } else {
      // Have negative values - need to handle specially
      return { 
        min: min * 1.1, // Add 10% padding below lowest negative value
        max: max > 0 ? max * 1.1 : 0 // Add padding to positive max or use 0 if all negative
      };
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={3}>
        {/* Metrics Table - Make even smaller */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }} elevation={3}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
              Financial Metrics {ticker ? `for ${ticker}` : ''}
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={financialPeriodType}
              onChange={(_event, nextType) => {
                if (nextType) setFinancialPeriodType(nextType);
              }}
              sx={{ mb: 1.25 }}
            >
              <ToggleButton value="quarterly">Quarterly</ToggleButton>
              <ToggleButton value="annual">Annual</ToggleButton>
            </ToggleButtonGroup>
            <TableContainer sx={{ maxHeight: '180px' }}>  {/* Reduced from 180px */}
              <Table aria-label="financial metrics table" size="small">  {/* Changed to small size */}
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>Metric</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        {selectedPeriodHeader}
                        <Tooltip title="After you click a bar, this column shows values for that selected period.">
                          <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{activePeriodHeader}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>Change</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {metrics.map((metric, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ fontSize: '0.85rem', py: 0.5 }}>{metric.Metric}</TableCell>
                      <TableCell sx={{ fontSize: '0.85rem', py: 0.5 }}>{metric["Clicked Period"]}</TableCell>
                      <TableCell 
                        sx={{ 
                          fontSize: '0.85rem',
                          py: 0.5,
                          color: comparisonMode && metric.Change ? 
                                  (parseFloat(metric.Change) > 0 ? 'green' : 
                                  parseFloat(metric.Change) < 0 ? 'red' : 'inherit') : 
                                  'inherit'
                        }}
                      >
                        {metric.Value}
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontSize: '0.85rem',
                          py: 0.5,
                          color: metric.Change && parseFloat(metric.Change) > 0 ? 'green' : 
                                 metric.Change && parseFloat(metric.Change) < 0 ? 'red' : 'inherit'
                        }}
                      >
                        {metric.Change}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Financial Chart - Make larger */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, height: '500px' }} elevation={3}> {/* Increased from 600px to 700px */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
                Financial Trend Chart
              </Typography>
              <Tooltip title="Hover a bar to preview that period in the table. Click a bar to lock comparison mode and compare all periods against it.">
                <InfoOutlinedIcon sx={{ fontSize: 18, color: 'info.main', cursor: 'help' }} />
              </Tooltip>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Hover over bars for a quick preview. Click any bar to compare all periods against that selected period.
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: '#8884d8' }} />
                <Typography variant="caption">Revenue</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: '#82ca9d' }} />
                <Typography variant="caption">Operating Income</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: 'blue' }} />
                <Typography variant="caption">Selected period (comparison mode)</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: 'green' }} />
                <Typography variant="caption">Higher than selected period</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: 'red' }} />
                <Typography variant="caption">Lower than selected period</Typography>
              </Box>
            </Box>
            
            {comparisonMode && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Button 
                  variant="contained" 
                  color="error" 
                  onClick={handleExitComparison}
                  sx={{ fontSize: '0.95rem' }}
                >
                  Exit Comparison Mode
                </Button>
              </Box>
            )}
            
            {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading financial data...</Typography>
              </Box>
            ) : financialData && financialData.length > 0 ? (
              <Box sx={{ height: '90%', width: '100%' }}> {/* Increased from 90% to 95% */}
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={financialData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    onMouseMove={handleBarHover}
                    onClick={handleBarClick}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="period" 
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      tick={{ fill: theme.palette.text.secondary }}
                      tickFormatter={(value) => {
                        // Remove time portion from date string
                        return value.split('T')[0];
                      }}
                    />
                    <YAxis 
                      tickFormatter={(value) => {
                        const numericValue = Number(value);
                        const symbol = getCurrencySymbol();
                        if (!Number.isFinite(numericValue)) return `${symbol}0.0`;
                        // Format based on value magnitude
                        if (Math.abs(numericValue) >= 1e9) return `${symbol}${(numericValue / 1e9).toFixed(1)}B`;
                        if (Math.abs(numericValue) >= 1e6) return `${symbol}${(numericValue / 1e6).toFixed(1)}M`;
                        if (Math.abs(numericValue) >= 1e3) return `${symbol}${(numericValue / 1e3).toFixed(1)}K`;
                        return `${symbol}${numericValue.toFixed(1)}`;
                      }}
                      label={{ value: `Value (${getCurrencySymbol()})`, angle: -90, position: 'insideLeft' }}
                      tickCount={7}
                      domain={[
                        Math.min(
                          getDataRange(financialData, 'Revenue').min, 
                          getDataRange(financialData, 'Operating Income').min
                        ), 
                        Math.max(
                          getDataRange(financialData, 'Revenue').max, 
                          getDataRange(financialData, 'Operating Income').max
                        )
                      ]}
                    />
                    <Tooltip 
                      formatter={(value, name) => {
                        const numericValue = Number(value);
                        const symbol = getCurrencySymbol();
                        if (!Number.isFinite(numericValue)) return [`${symbol}0.00`, name];
                        // Format based on value magnitude
                        if (Math.abs(numericValue) >= 1e9) return [`${symbol}${(numericValue / 1e9).toFixed(2)}B`, name];
                        if (Math.abs(numericValue) >= 1e6) return [`${symbol}${(numericValue / 1e6).toFixed(2)}M`, name];
                        if (Math.abs(numericValue) >= 1e3) return [`${symbol}${(numericValue / 1e3).toFixed(2)}K`, name];
                        return [`${symbol}${numericValue.toFixed(2)}`, name];
                      }}
                      labelFormatter={(label) => `Period: ${label}`}
                      contentStyle={{
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(17, 24, 39, 0.96)' : '#ffffff',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 8,
                      }}
                      labelStyle={{
                        color: theme.palette.mode === 'dark' ? '#f3f4f6' : '#111827',
                        fontWeight: 600,
                      }}
                      itemStyle={{
                        color: theme.palette.mode === 'dark' ? '#e5e7eb' : '#1f2937',
                      }}
                    />
                    <Legend wrapperStyle={{ paddingTop: 20 }} />
                    <Bar 
                      name={comparisonMode ? 'Revenue (Blue=Clicked, Green=Higher, Red=Lower)' : 'Revenue'} 
                      dataKey="Revenue" 
                      fill="#8884d8"
                      fillOpacity={0.8}
                      barSize={40}
                    >
                      {comparisonMode && financialData.map((entry, index) => {
                        const isSamePeriod = entry.period === clickedPeriod;
                        // Find the clicked data for comparison
                        const clickedData = financialData.find(d => d.period === clickedPeriod);
                        
                        // Default color
                        let color = '#8884d8';
                        
                        if (clickedData) {
                          if (isSamePeriod) {
                            // Clicked bar is blue
                            color = 'blue';
                          } else {
                            // Do explicit number comparison to ensure correct color assignment
                            const currentRevenue = Number(entry.Revenue) || 0;
                            const clickedRevenue = Number(clickedData.Revenue) || 0;
                            
                            if (currentRevenue > clickedRevenue) {
                              color = 'green'; // Higher revenue than clicked bar
                            } else if (currentRevenue < clickedRevenue) {
                              color = 'red'; // Lower revenue than clicked bar
                            }
                          }
                        }
                        
                        return <Cell key={`revenue-cell-${index}`} fill={color} />;
                      })}
                    </Bar>
                    <Bar 
                      name="Operating Income" 
                      dataKey="Operating Income" 
                      fill={comparisonMode ? '#999999' : '#82ca9d'} 
                      fillOpacity={comparisonMode ? 0.5 : 0.8}
                      barSize={40}
                    >
                      {financialData.map((entry, index) => {
                        if (comparisonMode) {
                          const isSamePeriod = entry.period === clickedPeriod;
                          // Find the clicked data for comparison
                          const clickedData = financialData.find(d => d.period === clickedPeriod);
                          
                          // Default color
                          let color = '#82ca9d';
                          
                          if (clickedData) {
                            if (isSamePeriod) {
                              // Clicked bar is blue
                              color = 'blue';
                            } else {
                              // Do explicit number comparison to ensure correct color assignment
                              const currentOperatingIncome = Number(entry['Operating Income']) || 0;
                              const clickedOperatingIncome = Number(clickedData['Operating Income']) || 0;
                              
                              if (currentOperatingIncome > clickedOperatingIncome) {
                                color = 'green'; // Higher operating income than clicked bar
                              } else if (currentOperatingIncome < clickedOperatingIncome) {
                                color = 'red'; // Lower operating income than clicked bar
                              }
                            }
                          }
                          
                          return <Cell key={`operating-income-cell-${index}`} fill={color} />;
                        } else {
                          // Not in comparison mode - change negative values to red
                          const operatingIncome = Number(entry['Operating Income']) || 0;
                          const color = operatingIncome < 0 ? "#ef4444" : "#82ca9d";
                          return <Cell key={`operating-income-cell-${index}`} fill={color} />;
                        }
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <Typography variant="h6" color="text.secondary">
                  No financial data available
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MetricTable;